import { useNavigate } from "react-router-dom";

interface NavigationOptions {
  openInNewTab?: boolean;
}

/**
 * Custom hook that provides a navigation handler which scrolls to the top of the page
 * before navigating to a new route. If a hash is present in the path, it will scroll
 * smoothly to the element with the corresponding ID after navigation.
 *
 * @returns {Function} handleNavigation - The navigation handler function.
 *
 * @example
 * const navigateTo = useScrollToTopAndNavigate();
 * navigateTo('/some-path#section-id');
 *
 * @param {string} path - The path to navigate to. Can include a hash for scrolling to a specific element.
 * @param {NavigationOptions} [options] - Optional navigation options.
 * @param {boolean} [options.openInNewTab] - If true, opens the path in a new tab.
 */
const useScrollToTopAndNavigate = () => {
  const navigate = useNavigate();

  const handleNavigation = (path: string, options?: NavigationOptions) => {
    const [basePath, hash] = path.split("#");

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    setTimeout(() => {
      if (options?.openInNewTab) {
        window.open(path, "_blank");
      } else {
        navigate(basePath);
      }
    }, 500);

    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 600);
    }
  };

  return handleNavigation;
};

export default useScrollToTopAndNavigate;
